import { ChangeDetectionStrategy, Component, DestroyRef, input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Observable, switchMap } from 'rxjs';
import { BomItemStagesState, BomItemStagesStore } from './bom-item-stages.store';
import { BomItemStagesDto, StageDto, StageFact } from './bom-item-stages.dto';

@Component({
  selector: 'bom-item-stages',
  templateUrl: './bom-item-stages.component.html',
  styleUrl: './bom-item-stages.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe]
})
// The card follows the bomItemId input: every change re-inits the store (switchMap drops stale in-flight responses)
export class BomItemStagesComponent implements OnDestroy {
  readonly bomItemId = input.required<number | undefined>();

  // undefined when the selected node is the tree's root
  readonly parentBomItemId = input.required<number | undefined>();

  readonly stageFact = StageFact;

  readonly state$: Observable<BomItemStagesState>;

  constructor(
    private store: BomItemStagesStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    toObservable(this.bomItemId)
      .pipe(
        switchMap(bomItemId => this.store.init(bomItemId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // A node under a parent is one part. The root has no parent to pick by, so it lists the
  // item in every place it is used — the route depends on the parent.
  visibleParts(state: BomItemStagesState): BomItemStagesDto[] {
    const parts = state.parts ?? [];
    const parentBomItemId = this.parentBomItemId();

    return parentBomItemId === undefined
      ? parts
      : parts.filter(part => part.parentBomItemId === parentBomItemId);
  }

  // the evidence behind a stage: what was said last, when, and on which order
  stageTitle(stage: StageDto): string {
    if (stage.fact === null) {
      return 'brak danych';
    }

    if (stage.fact === StageFact.skippedByDefault) {
      return 'pomijany · część kupiona';
    }

    const date = formatDate(stage.factAt!);
    const order = `zam. ${stage.orderNumber}`;

    switch (stage.fact) {
      case StageFact.reported:
        return `zgłoszony ${date} · ${order}`;
      case StageFact.skipped:
        return `pomijany od ${date} · ${order}`;
      case StageFact.restored:
        return `przywrócony ${date} · ${order}`;
    }
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
}
